// react
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import KeyIcon from '@mui/icons-material/Key';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

// api
import {
  useGetDaluxApiKeyQuery,
  useSaveDaluxApiKeyMutation,
} from '@/api/dalux-integration/daluxIntegrationApiSlice';

// shared components
import { SubmitButton } from '@/components/ui/button';
import { RHFTextField } from '@/components/ui/form';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import type { DaluxApiKeyRecord } from './types';

import dayjs from 'dayjs';
type DaluxApiKeyCardProps = {
  accountId: string;
};

type DaluxApiKeyFormValues = {
  apiKey: string;
  expiresAt: string;
};

export default function DaluxApiKeyCard({ accountId }: DaluxApiKeyCardProps) {
  const { t } = useTranslation('dalux-integration');
  const dispatch = useDispatch();

  const [isReplacing, setIsReplacing] = useState(false);

  const { control, handleSubmit, reset } = useForm<DaluxApiKeyFormValues>({
    defaultValues: {
      apiKey: '',
      expiresAt: '',
    },
  });

  const apiKeyValue = useWatch({ control, name: 'apiKey' });

  const { data: keyData, isLoading } = useGetDaluxApiKeyQuery(accountId ?? '', {
    skip: !accountId,
  });
  const daluxApiKey =
    keyData && typeof keyData === 'object'
      ? (keyData as DaluxApiKeyRecord)
      : undefined;

  const [saveKey, { isLoading: isSaving }] = useSaveDaluxApiKeyMutation();

  const hasApiKey = daluxApiKey?.hasApiKey;
  const showForm = !hasApiKey || isReplacing;

  const handleSave = handleSubmit(async (values) => {
    try {
      await saveKey({
        accountId,
        apiKey: values.apiKey,
        expiresAt: values.expiresAt || null,
      }).unwrap();
      reset();
      setIsReplacing(false);
      dispatch(
        showSnackbar({ message: t('api_key.saved'), severity: 'success' }),
      );
    } catch {
      dispatch(
        showSnackbar({ message: t('api_key.save_error'), severity: 'error' }),
      );
    }
  });

  return (
    <Card>
      <CardHeader
        avatar={<KeyIcon color="primary" />}
        title={t('api_key.title')}
      />
      <CardContent>
        <Stack spacing={2}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : hasApiKey && !showForm ? (
            <>
              <Box>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <CheckCircleRoundedIcon color="success" />
                  <Typography variant="body2" gutterBottom ml={2}>
                    {t('api_key.existing')}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {daluxApiKey.expiresAt && (
                    <Chip
                      label={`${t('api_key.expires_at')}: ${dayjs(daluxApiKey.expiresAt).format('DD/MM/YYYY')}`}
                      size="small"
                      color={
                        dayjs(daluxApiKey.expiresAt).isBefore(dayjs())
                          ? 'error'
                          : 'default'
                      }
                    />
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsReplacing(true)}
                >
                  {t('api_key.replace')}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('api_key.description')}
              </Typography>

              <RHFTextField
                name="apiKey"
                control={control}
                label={t('api_key.label')}
                size="small"
                type="password"
                fullWidth
              />
              <RHFTextField
                name="expiresAt"
                control={control}
                label={t('api_key.expires_label')}
                size="small"
                type="date"
                fullWidth
              />
              <Stack direction="row" spacing={1}>
                <SubmitButton
                  onClick={handleSave}
                  disabled={!apiKeyValue || isSaving}
                >
                  {t('api_key.save')}
                </SubmitButton>
                {isReplacing && (
                  <Button
                    variant="outlined"
                    onClick={() => setIsReplacing(false)}
                    disabled={isSaving}
                  >
                    {t('api_key.cancel')}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
